import React, {
	memo,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { CollapsibleBlock, Select } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection/ProfileTabSection';
import EmptyStubKpi from '@components/Map/SitesInfo/EmptyStubKpi';
import MultiSiteParameters from '@components/Map/SitesInfo/SiteParameters/MultiSiteParameters';

import { useQueryActiveSitesData } from '@hooks/sites/useQueryActiveSitesData';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';
import { useSiteCells } from '@hooks/useSiteCells';

import { auth, kpi, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSelectedVendorOnSitesCard } from '@slices/map';

import { sortObjectByKey } from '@utils/common';
import { generateSelectOptions } from '@utils/generateSelectOptions/generateSelectOptions';
import { toggleCollapsibleBlockMapInfo } from '@utils/toggleCollapsibleBlockMapInfo';

import { TTechnologyNameTitle } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { TVendor } from '@interfaces/map/map';

import {
	ETechnologiesValues,
	EVendorsValues,
	TTechnologiesValues,
	TVendorsLabels,
	TVendorsValues,
} from '@enums/common';

import { collapsibleBlocksInitialState } from '@components/Map/SitesInfo/constants';

import styles from './SelectedSiteInfo.module.scss';

interface IMultipleSitesInfoProps {
	parametersOnMapComponent: ReactNode;
}

const kpiAnalysisSection = 'kpiAnalysis';

const MultipleSitesInfo = ({ parametersOnMapComponent }: IMultipleSitesInfoProps) => {
	const [
		collapsibleBlocks,
		setCollapsibleBlocks,
	] = useState<Record<string, boolean>>(() => ({
		...collapsibleBlocksInitialState,
	}));

	const dispatch = useAppDispatch();

	const { accessPermission } = useAppSelector(auth);
	const { activeSitesVendors, selectedVendorOnSitesCard } = useAppSelector(map);
	const { isKpiSourceClicked } = useAppSelector(kpi);

	useEffect(() => {
		if (isKpiSourceClicked) {
			setCollapsibleBlocks({
				...collapsibleBlocksInitialState,
				[kpiAnalysisSection]: true,
			});
		}
	}, [isKpiSourceClicked]);

	const {
		networkOperatorKpiUpdater,
		networkOperatorKpiViewer,
		assetConsumerOrganizationAdministrator,
	} = accessPermission;

	const isKpiView = (
		networkOperatorKpiUpdater
		|| networkOperatorKpiViewer
		|| assetConsumerOrganizationAdministrator
	);

	const [currentTab, setCurrentTab] = useState<TTechnologiesValues>();
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption>();

	const { vendors: allVendors } = useManageSitesOptions();
	const { activeSites } = useQueryActiveSitesData();

	const { limenetCells, nokiaCells } = useSiteCells({
		sites: activeSites,
		technology: currentTab,
	});

	const uniqueSiteTechs = useMemo(() => {
		return activeSites.reduce<Array<TTechnologyNameTitle>>((acc, site) => {
			if (site.vendor.name !== selectedVendor?.value) return acc;

			site.technologies.forEach((technology) => {
				if (!acc.find((accTechnology) => accTechnology.name === technology.name)) {
					acc.push(technology);
				}
			});

			return acc;
		}, []);
	}, [activeSites, selectedVendor]);

	const tabs = useMemo(() => {
		return sortObjectByKey(uniqueSiteTechs.map((technology) => ({
			title: technology.title,
			value: technology.name,
			isVisible: true,
		})), 'title');
	}, [uniqueSiteTechs]);

	useEffect(() => {
		if (tabs?.length) setCurrentTab(tabs[0].value);
	}, [tabs]);

	const toggleCollapsibleBlock = (name: string) => toggleCollapsibleBlockMapInfo({
		name,
		collapsibleBlocks,
		setCollapsibleBlocks,
		initialState: collapsibleBlocksInitialState,
	});

	const getCurrentTab = useCallback(() => {
		if (!currentTab) return null;

		const filteredSites = activeSites.filter((site) => {
			const isSiteHasCurrentTechnology = site.technologies.find((technology) => technology.name === currentTab);

			return isSiteHasCurrentTechnology && site.vendor.name === selectedVendor?.value;
		});

		return ETechnologiesValues[currentTab] && (
			<MultiSiteParameters
				cells={[...limenetCells, ...nokiaCells]}
				sites={filteredSites}
			/>
		);
	}, [
		activeSites,
		currentTab,
		limenetCells,
		selectedVendor?.value,
		nokiaCells,
	]);

	const vendorsOptions = useMemo(() => {
		if (!allVendors?.length) return [];

		const vendors = activeSitesVendors.map((vendor) => {
			const vendorTitle = allVendors.find((item: TVendor) => item.name === vendor)?.title;

			return {
				title: vendorTitle,
				value: vendor,
			};
		});

		const vendorsOptions = generateSelectOptions({
			data: vendors,
			value: 'value',
			label: 'title',
			sort: true,
		});

		setSelectedVendor(vendorsOptions[0]);

		return vendorsOptions;
	}, [activeSitesVendors, allVendors]);

	const handleChangeVendor = (option: TSelectOption) => {
		setSelectedVendor(option);
	};

	useEffect(() => {
		if (selectedVendor) {
			dispatch(setSelectedVendorOnSitesCard({
				name: selectedVendor.value as TVendorsValues,
				title: selectedVendor.label as TVendorsLabels,
			}));

			if (selectedVendor.value === EVendorsValues.nokia) {
				setCollapsibleBlocks({
					...collapsibleBlocksInitialState,
					[kpiAnalysisSection]: false,
				});
			}
		}
	}, [dispatch, selectedVendor]);

	return (
		<div className={styles.siteInfoContainer}>
			<div className={styles.type}>
				Selected
			</div>
			<div className={styles.generalInfoContainer}>
				<div><span className={styles.fieldName}>Total vendors: </span>{activeSitesVendors.length}</div>
				<div><span className={styles.fieldName}>Total sites: </span>{activeSites.length}</div>
			</div>
			<hr />
			<div>
				<Select
					handleChange={handleChangeVendor}
					isFilter
					isWithoutUseForms
					label='Vendors'
					name='active-sites-vendors'
					options={vendorsOptions}
					value={selectedVendor}
				/>
			</div>
			<CollapsibleBlock
				isClickableRow
				isOpen={collapsibleBlocks.siteParameters}
				label='Site Parameters'
				name='siteParameters'
				toggleState={toggleCollapsibleBlock}
				withSeparatorLine
			>
				<div className={styles.technologyTabs}>
					<ProfileTabSection
						currentTab={currentTab}
						getCurrentTab={getCurrentTab}
						isFullWidthTabs
						isWithoutShadow
						setCurrentTab={setCurrentTab}
						tabs={tabs}
					/>
				</div>
			</CollapsibleBlock>
			{isKpiView &&
				<CollapsibleBlock
					isClickableRow
					isDisabled={selectedVendorOnSitesCard?.name === EVendorsValues.nokia}
					isOpen={collapsibleBlocks.kpiAnalysis}
					label={<div className={styles.collapsibleLabel}>KPI Analysis</div>}
					name={kpiAnalysisSection}
					toggleState={toggleCollapsibleBlock}
					withSeparatorLine
				>
					{parametersOnMapComponent || <EmptyStubKpi />}
				</CollapsibleBlock>
			}
		</div>
	);
};

export default memo(MultipleSitesInfo);
